/**
 * Vidu 核心业务 API 封装层
 *
 * 封装所有与 Vidu 核心业务相关的 API 调用
 * @/app/hooks 必须通过此层调用,不能直接引用 @/app/services/vidu
 */

import {
  activitiesGetWork,
  activitiesListActivities,
  activitiesSignupActivity,
  clipCreateProject,
  clipDeleteProject,
  clipExportByProject,
  clipGetProject,
  clipListJobs,
  clipListProjects,
  clipRegisterMedia,
  clipUpdateProject,
  creationsDetail,
  earnCreateWithdraw,
  earnGetActivity,
  earnGetMy,
  earnListCampions,
  earnListEarnActivities,
  earnListMySubmits,
  earnSubmitApply,
  earnSubmitWork,
  inspirationsGetInspirationDetail,
  materialsBatchDeleteElement,
  materialsCreateElementCheck,
  materialsGetMotion,
  materialsGetVoice,
  materialsListLabels,
  materialsListVoices,
  materialsUpdateVoice,
  missionCenterGetMissionQuota,
  projectsGetViralGrowthInfo,
  tasksCreate,
  tasksCreateByCraft,
  tasksCreateByTool,
  tasksGet,
  tasksGetTaskCredits,
  tasksGetTeamQuota,
  usersGetPersona,
  viduExploreDetailPro,
  viduFeedList,
  viduGetCost,
  viduGetPageConfig,
} from '@/app/services/vidu';

// 导出类型(供 hooks 使用)
export type {
  CraftifyV1EarnSocialAccount,
  ViduV1Activity,
  ViduV1Campion,
  ViduV1CreateEarnWithdrawRequest,
  ViduV1EarnApply,
  ViduV1EarnSubmit,
  ViduV1EarnWithdraw,
  ViduV1ReportAdEventRequest,
} from '@/app/services/vidu';

export { activitiesGetWork, viduExploreDetailPro, viduFeedList };
export {
  activitiesSignupActivity,
  earnCreateWithdraw,
  earnGetActivity,
  earnGetMy,
  earnListCampions,
  earnListEarnActivities as earnListActivities,
  earnListMySubmits,
  earnSubmitApply,
  earnSubmitWork,
};
export {
  clipCreateProject,
  clipDeleteProject,
  clipExportByProject,
  clipGetProject,
  clipListJobs,
  clipListProjects,
  clipRegisterMedia,
  clipUpdateProject,
};

/**
 * 获取生成物详情
 */
export const getCreationDetail = creationsDetail;

/**
 * 获取活动作品详情
 */
export const getWorkDetail = activitiesGetWork;

/**
 * 获取公开用户信息
 */
export const getUserPersona = usersGetPersona;

/**
 * 获取灵感详情
 */
export const getInspirationDetail = inspirationsGetInspirationDetail;

/**
 * 检查是否可以创建元素
 */
export const checkEnableCreateElement = materialsCreateElementCheck;

/**
 * 获取我的动作详情
 */
export const getMotionDetail = materialsGetMotion;

/**
 * 获取素材标签列表
 */
export const listMaterialLabels = materialsListLabels;

/**
 * 获取当前用户的自定义音色列表
 */
export const listVoices = materialsListVoices;

/**
 * 获取自定义音色详情
 */
export const getVoiceDetail = materialsGetVoice;

/**
 * 更新自定义音色信息
 */
export const updateVoice = materialsUpdateVoice;

/**
 * 获取任务配额(用于广告任务)
 */
export const getMissionQuota = missionCenterGetMissionQuota;

/**
 * 获取病毒式增长信息
 */
export const getViralGrowthInfo = projectsGetViralGrowthInfo;

/**
 * 创建任务
 */
export const createTask = tasksCreate;

/**
 * 通过 Craft 创建任务
 */
export const createTaskByCraft = tasksCreateByCraft;

/**
 * 获取任务详情
 */
export const getTaskDetail = tasksGet;

/**
 * 获取任务所需积分
 */
export const getTaskCredits = tasksGetTaskCredits;

/** 获取 One-Click MV V2 所需积分 */
export const getTaskCreditsV2: typeof tasksGetTaskCredits = tasksGetTaskCredits;

/**
 * 获取 Vidu 计价（通用 GET /vidu/v1/cost，query 使用 biz_code）
 */
export const getViduCost = viduGetCost;

/**
 * 获取页面配置
 */
export const getPageConfig = viduGetPageConfig;

/**
 * 获取活动列表
 */
export const listActivities = activitiesListActivities;

/**
 * 批量删除元素
 */
export const batchDeleteElement = materialsBatchDeleteElement;

/**
 * 通过工具创建任务
 */
export const createTaskByTool = tasksCreateByTool;

const isToolRouteCodecError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;

  const { reason, message } = error as { reason?: string; message?: string };
  return reason === 'CODEC' && !!message?.includes('parsing field "id"') && message.includes('parsing "tool"');
};

/**
 * 通过工具创建任务（One-Click MV V2 兼容入口）。
 * 部分开发环境未注册 /tasks/tool，服务端会把 tool 错当成任务 id；此时回退到同服务的标准创建入口。
 */
export const createTaskByToolV2 = (async (options: Parameters<typeof tasksCreateByTool>[0]) => {
  const result = await tasksCreateByTool(options);

  const error = 'error' in result ? result.error : undefined;
  if (!isToolRouteCodecError(error)) {
    return result;
  }

  return tasksCreate(options);
}) as typeof tasksCreateByTool;

/**
 * 获取团队配额
 */
export const getTeamQuota = tasksGetTeamQuota;
